import { Calendar, MoreVertical, Clock } from "lucide-react";
import { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  deleteDoc,
  updateDoc,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import Modal from "./Modal";

function getEstadoColor(estado) {
  if (estado === "activa") return "bg-green-100 text-green-700";
  if (estado === "prueba") return "bg-yellow-100 text-yellow-700";
  if (estado === "cancelada") return "bg-red-100 text-red-700";
  return "bg-gray-100 text-gray-700";
}

function diasHasta(fecha) {
  const hoy = new Date();
  const target = new Date(fecha);
  const diff = Math.ceil((target - hoy) / (1000 * 60 * 60 * 24));
  return diff;
}

export default function Subscriptions() {
  const [suscripciones, setSuscripciones] = useState([]);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [editSuscripcion, setEditSuscripcion] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const user = auth.currentUser;

  useEffect(() => {
    if (!user) return setSuscripciones([]);
    const q = query(collection(db, "suscripciones"), where("uid", "==", user.uid));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setSuscripciones(docs);
    });
    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    if (!user || suscripciones.length === 0) return;

    suscripciones.forEach(async (s) => {
      const today = new Date();
      const pago = new Date(s.proximoPago);

      if (s.estado === "cancelada" && pago <= today) {
        await deleteDoc(doc(db, "suscripciones", s.id));
        return;
      }

      if (s.estado === "activa" && pago <= today) {
        const nuevoPago = new Date(pago);
        nuevoPago.setMonth(nuevoPago.getMonth() + 1);
        await updateDoc(doc(db, "suscripciones", s.id), {
          proximoPago: nuevoPago.toISOString(),
        });
        return;
      }

      if (s.estado === "prueba" && s.termina) {
        const finPrueba = new Date(s.termina);
        if (finPrueba <= today) {
          const nuevoEstado = s.metodoPago ? "activa" : "cancelada";
          let nuevoPago = s.proximoPago;
          if (nuevoEstado === "activa") {
            const pagoPrueba = new Date(s.proximoPago);
            pagoPrueba.setMonth(pagoPrueba.getMonth() + 1);
            nuevoPago = pagoPrueba.toISOString();
          }
          await updateDoc(doc(db, "suscripciones", s.id), {
            estado: nuevoEstado,
            proximoPago: nuevoPago,
          });
        }
      }
    });
  }, [suscripciones, user]);

  const handleDelete = async (id) => {
    await deleteDoc(doc(db, "suscripciones", id));
    setMenuOpenId(null);
  };

  const handleEdit = (suscripcion) => {
    setEditSuscripcion(suscripcion);
    setShowModal(true);
    setMenuOpenId(null);
  };

  const handleUpdateSuscripcion = async (data) => {
    if (!editSuscripcion) return;
    await updateDoc(doc(db, "suscripciones", editSuscripcion.id), data);
    setShowModal(false);
    setEditSuscripcion(null);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-12 w-full max-w-6xl xl:max-w-[1200px] 2xl:max-w-[1400px] mx-auto">
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Suscripciones activas */}
        <div className="flex-1">
          <h2 className="text-2xl font-bold mb-6">
            Suscripciones Activas ({suscripciones.length})
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {suscripciones.map((s) => (
              <div
                key={s.id}
                className="bg-white rounded-xl shadow p-6 flex flex-col gap-2 relative min-h-[170px]"
              >
                <div className="absolute -top-5 -left-5 bg-blue-500 text-white rounded-full w-12 h-12 flex items-center justify-center text-2xl font-bold">
                  {s.nombre[0]}
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-lg font-semibold">{s.nombre}</div>
                    <div className="text-sm text-gray-500">{s.categoria}</div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${getEstadoColor(
                      s.estado
                    )}`}
                  >
                    {s.estado === "activa" && "Activa"}
                    {s.estado === "prueba" && "Prueba"}
                    {s.estado === "cancelada" && "Cancelada"}
                  </span>
                  <div className="relative">
                    <button
                      className="p-1"
                      onClick={() =>
                        setMenuOpenId(menuOpenId === s.id ? null : s.id)
                      }
                    >
                      <MoreVertical className="w-5 h-5 text-gray-400 cursor-pointer" />
                    </button>
                    {menuOpenId === s.id && (
                      <div className="absolute right-0 mt-2 bg-white border rounded shadow-lg z-10 w-32">
                        <button
                          className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                          onClick={() => handleEdit(s)}
                        >
                          Editar
                        </button>
                        <button
                          className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm text-red-600"
                          onClick={() => handleDelete(s.id)}
                        >
                          Eliminar
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-green-600 font-bold text-xl">
                    {Number(s.precio).toFixed(2)}
                    {s.moneda === "EUR" ? "€" : s.moneda}
                  </span>
                  <span className="text-gray-500 text-sm">/mes</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600 mt-2">
                  <Calendar className="w-4 h-4" />
                  Próximo pago:{" "}
                  <span className="font-semibold text-indigo-700">
                    {new Date(s.proximoPago).toLocaleDateString("es-ES", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                </div>
                {s.estado === "prueba" && s.termina && (
                  <div className="flex items-center gap-2 text-sm text-yellow-700 mt-1">
                    <Clock className="w-4 h-4" />
                    Termina el{" "}
                    {new Date(s.termina).toLocaleDateString("es-ES", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Próximos pagos */}
        <div className="bg-white rounded-xl shadow p-6 w-full lg:w-[280px] xl:w-[350px] mt-6 lg:mt-0">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-indigo-500" />
            Próximos Pagos
          </h3>
          <div className="flex flex-col gap-4">
            {suscripciones
              .slice()
              .sort((a, b) => new Date(a.proximoPago) - new Date(b.proximoPago))
              .map((s) => (
                <div key={s.id} className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <span
                      className={`w-3 h-3 rounded-full ${
                        s.estado === "activa"
                          ? "bg-blue-500"
                          : s.estado === "prueba"
                          ? "bg-yellow-500"
                          : "bg-gray-400"
                      }`}
                    ></span>
                    <span className="font-medium">{s.nombre}</span>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="font-bold text-gray-700">
                      {Number(s.precio).toFixed(2)}
                      {s.moneda === "EUR" ? "€" : s.moneda}
                    </span>
                    <span className="text-xs text-gray-500">
                      En {diasHasta(s.proximoPago)} días
                    </span>
                    <span
                      className={`text-xs font-semibold ${
                        diasHasta(s.proximoPago) < 10
                          ? "text-yellow-600"
                          : "text-blue-600"
                      }`}
                    >
                      {diasHasta(s.proximoPago) < 10
                        ? "Vence pronto"
                        : "Próximo"}
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {showModal && (
          <Modal
            suscripcion={editSuscripcion}
            onClose={() => {
              setShowModal(false);
              setEditSuscripcion(null);
            }}
            onSubmit={handleUpdateSuscripcion}
          />
        )}
      </div>
    </div>
  );
}
